import { Box, Divider, Stack, Typography } from '@mui/material'
import { AppAvatar } from 'components/core/avatar'
import { C1CopyText } from 'components/core/copytext/C1CopyText'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { C1Icon } from 'components/core/icons/C1Icon'
import { ExternalLink } from 'components/core/links/external-link'
import { MaxGrantDurationTooltip } from 'components/core/time/duration'
import { RoundedDuration } from 'components/core/time/duration/duration'
import { VariableText } from 'components/core/variable-text'
import { C1EntitlementDisplayName } from 'components/pages/entitlement/display-name'
import { InfoDrawer } from 'components/pages/explore/visibility/info-drawers/InfoDrawer'
import { InfoDrawerDetail, InfoDrawerDetailProps } from 'components/pages/explore/visibility/info-drawers/InfoDrawerDetail'
import { AttributeValueDisplayWithFetch } from 'components/pages/explore/visibility/info-drawers/parts/AttributeValueDisplayWithFetch'
import { InfoDrawerStyles } from 'components/pages/explore/visibility/info-drawers/styles'
import { CatalogProvider } from 'components/pages/requestcatalog/details/context/CatalogContext'
import { CatalogEntitlementList } from 'components/pages/requestcatalog/details/setup/catalog_entitlement_list'
import { AppEntitlementValue } from 'lib/actions/apps/app-entitlement'
import { isCatalogResourceType, RoleID } from 'lib/constants/system'
import { UserContext } from 'lib/context/app'
import { Minus } from 'lucide-react'
import { SearchAttributeValuesRequest } from 'pbts/c1/api/attribute/v1/attribute'
import { Duration } from 'pbts/google/protobuf/duration'
import { useContext } from 'react'
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'

const Content = ({ entitlement }: { entitlement: AppEntitlementValue }): React.ReactElement => {
    const intl = useIntl()
    const userContext = useContext(UserContext)
    const isSuperAdmin = userContext.user.roleIds.includes(RoleID.SuperAdmin)

    let durationGrant: Duration | undefined
    if (entitlement.maxGrantDuration.oneofKind === 'durationGrant') {
        durationGrant = Duration.create({ seconds: entitlement.maxGrantDuration.durationGrant.seconds })
    }

    const fields: InfoDrawerDetailProps = {
        fields: [{
            label: <FormattedMessage id="entitlement_application" defaultMessage="Application" />,
            value: isSuperAdmin ? <ExternalLink href={`/admin/applications/${entitlement.appId}`}>
                <Stack spacing={1} direction="row" alignItems="center">
                    <AppAvatar appName={entitlement.app?.displayName} />
                    <Typography noWrap title={entitlement.app?.displayName}>{entitlement.app?.displayName}</Typography>
                </Stack>
            </ExternalLink> : <Stack spacing={1} direction="row" alignItems="center">
                <AppAvatar appName={entitlement.app?.displayName} />
                <Typography noWrap title={entitlement.app?.displayName}>{entitlement.app?.displayName}</Typography>
            </Stack>,
        }, {
            label: <FormattedMessage id="entitlement_resource" defaultMessage="Resource" />,
            value: entitlement.appResource
                ? isSuperAdmin ? <ExternalLink href={`/admin/applications/${entitlement.appId}/resources/${entitlement.appResourceTypeId}/${entitlement.appResourceId}`}>
                    <Stack spacing={1} direction="row" alignItems="center">
                        <Typography noWrap title={entitlement.appResource?.displayName}>
                            {entitlement.appResource?.displayName}
                        </Typography>
                    </Stack>
                </ExternalLink> : <Stack spacing={1} direction="row" alignItems="center">
                    <Typography noWrap title={entitlement.appResource?.displayName}>
                        {entitlement.appResource?.displayName}
                    </Typography>
                </Stack>
                : <C1Icon icon={Minus} size="sm" sx={{ color: 'text.disabled' }} />,

        }, {
            label: <FormattedMessage id="entitlement_resource_type" defaultMessage="Resource type" />,
            value: <Typography>{entitlement.appResourceType?.displayName}</Typography>,
        }, {
            label: <FormattedMessage id="entitlement_slug" defaultMessage="Slug" />,
            value: <VariableText
                variableText={entitlement.slug}
                emptyState={intl.formatMessage({
                    id: 'entitlement_details_no_slug',
                    defaultMessage: 'None',
                })}
            />,
        }, {
            label: <FormattedMessage id="entitlement_details_display_name" defaultMessage="Display name" />,
            value: entitlement.appResource
                ? <Typography>{`${entitlement.appResource?.displayName} ${entitlement.slug}`}</Typography>
                : <C1Icon icon={Minus} size="sm" sx={{ color: 'text.disabled' }} />,
        }, {
            label: <FormattedMessage id="entitlement_details_source" defaultMessage="Entitlement source" />,
            value: entitlement.systemBuiltin
                ? <FormattedMessage id="entitlement_source_builtin" defaultMessage="System managed" />
                : <FormattedMessage id="entitlement_source_connector" defaultMessage="Connector managed" />,
        }, {
            label: <FormattedMessage id="entitlement_details_id" defaultMessage="Entitlement ID" />,
            value: <C1CopyText value={entitlement.id} typographyVariant="monospace" truncateWidth="12em" />,
        }, {
            label: <FormattedMessage id="entitlement_details_description" defaultMessage="Description" />,
            value: <Typography>{entitlement.description}</Typography>,
        }, {
            label: <FormattedMessage id="entitlement_details_risk_level" defaultMessage="Risk level" />,
            value: entitlement.riskLevelValueId
                ? <AttributeValueDisplayWithFetch
                    req={SearchAttributeValuesRequest.create({
                        ids: [entitlement.riskLevelValueId],
                    })}
                />
                : <C1Icon icon={Minus} size="sm" sx={{ color: 'text.disabled' }} />,
        }, {
            label: <FormattedMessage id="entitlement_details_compliance_framework" defaultMessage="Compliance framework" />,
            value: entitlement.complianceFrameworkValueIds.length
                ? <AttributeValueDisplayWithFetch
                    req={SearchAttributeValuesRequest.create({
                        ids: entitlement.complianceFrameworkValueIds,
                    })}
                />
                : <C1EmptyIndicator />,
        }, {
            label: <FormattedMessage id="entitlement_details_grant_count" defaultMessage="Grant count" />,
            value: <Typography><FormattedNumber value={entitlement.grantCount} /></Typography>,
        }, {
            label: <Stack direction="row" spacing={1} alignItems="center">
                <Typography>
                    <FormattedMessage
                        id="entitlement_max_request_duration"
                        defaultMessage="Max request duration"
                    />
                </Typography>
                <MaxGrantDurationTooltip />
            </Stack>,
            value: <RoundedDuration duration={durationGrant} />,
        }],
    }

    return <InfoDrawerDetail fields={fields.fields} />
}

export const AppEntitlementInfoDrawer = ({ entitlement }: { entitlement: AppEntitlementValue }): React.ReactElement => {
    return <InfoDrawer
        title={<Stack direction="column" spacing={2} flex={1}>
            <Typography variant="h5" component="div">
                <FormattedMessage id="app_entitlement_node_info_drawer_title" defaultMessage="Entitlement detail" />
            </Typography>
            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                p={2}
                sx={InfoDrawerStyles.objectContainer}
            >
                <C1EntitlementDisplayName entitlement={entitlement} />
            </Stack>
        </Stack>}
        content={<Stack spacing={3}>
            <Content entitlement={entitlement} />
            {isCatalogResourceType(entitlement.appResourceTypeId) && <>
                <Divider />
                <Stack spacing={2} px={2}>
                    <Typography variant="h5" component="div">
                        <FormattedMessage id="app_entitlement_node_info_drawer_entitlements_title" defaultMessage="Entitlements" />
                    </Typography>
                    <Box>
                        <CatalogProvider value={{ requestCatalogId: entitlement.id, memberCount: entitlement.grantCount }}>
                            <CatalogEntitlementList
                                requestCatalogId={entitlement.id}
                                memberCount={entitlement.grantCount}
                                hideEnrolledStatus
                            />
                        </CatalogProvider>
                    </Box>
                </Stack>
            </>}
        </Stack>}
    />
}
